if (!customElements.get('purchase-options')) {
  customElements.define(
    'purchase-options',
    class PurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.formElement = this.querySelector('[data-purchase-form]');
        if (!this.formElement) return;

        this.submitButtonElement = this.querySelector('[data-purchase-submit]');
        this.spinnerElement = this.querySelector('[data-purchase-spinner]');
        this.priceElement = this.querySelector('[data-purchase-submit-price]');
        this.comparePriceElement = this.querySelector('[data-purchase-submit-compare-price]');
        this.errorElement = this.querySelector('[data-purchase-error]');
        this.cartElement = document.querySelector('cart-drawer');

        this.formElement.addEventListener('change', this.onChange.bind(this));
        this.formElement.addEventListener('submit', this.onSubmit.bind(this));
      }

      onChange(event) {
        if (event.target.name === 'id') this.updateOption(event.target);
        if (event.target.name === 'gift') this.updateGift(event.target);
      }

      updateOption(inputElement) {
        this.querySelectorAll('[data-purchase-option]').forEach((optionElement) => {
          optionElement.classList.toggle('product-purchase__option--selected', optionElement.contains(inputElement));
        });

        if (this.priceElement) this.priceElement.textContent = inputElement.dataset.price;
        if (!this.comparePriceElement) return;

        const comparePrice = inputElement.dataset.comparePrice;
        this.comparePriceElement.textContent = comparePrice || '';
        this.comparePriceElement.hidden = !comparePrice;
      }

      updateGift(inputElement) {
        this.querySelectorAll('[data-purchase-gift]').forEach((giftElement) => {
          giftElement.classList.toggle('product-purchase__gift--selected', giftElement.contains(inputElement));
        });
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButtonElement.getAttribute('aria-disabled') === 'true') return;

        const selectedOptionElement = this.formElement.querySelector('input[name="id"]:checked');
        if (!selectedOptionElement) return;

        this.setSubmitLoading(true);
        this.showError('');

        try {
          await this.clearGifts();
          const response = await this.addToCart(selectedOptionElement);

          if (response.status) {
            this.showError(response.description || response.message);
            return;
          }

          if (this.cartElement) {
            this.cartElement.renderContents(response);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setSubmitLoading(false);
        }
      }

      async clearGifts() {
        const response = await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' });
        const cartData = await response.json();

        const giftLines = cartData.items.filter((line) => line.properties && line.properties._gift_product);
        if (giftLines.length === 0) return;

        const formData = new FormData();
        giftLines.forEach((line) => {
          formData.append(`updates[${line.key}]`, 0);
        });

        const config = fetchConfig();
        delete config.headers['Content-Type'];

        await fetch(`${window.routes.cart_update_url}.js`, {
          ...config,
          body: formData,
        });
      }

      async addToCart(selectedOptionElement) {
        const formData = new FormData();

        formData.append('items[0][id]', selectedOptionElement.value);
        formData.append('items[0][quantity]', 1);

        if (selectedOptionElement.dataset.sellingPlan) {
          formData.append('items[0][selling_plan]', selectedOptionElement.dataset.sellingPlan);
        }

        if (selectedOptionElement.dataset.optionName) {
          formData.append('items[0][properties][_purchase_option]', selectedOptionElement.dataset.optionName);
        }

        const selectedGiftElement = this.formElement.querySelector('input[name="gift"]:checked');
        if (selectedGiftElement) {
          formData.append('items[1][id]', selectedGiftElement.value);
          formData.append('items[1][quantity]', 1);
          formData.append('items[1][properties][_gift_product]', 'true');
        }

        if (this.cartElement) {
          const sectionIds = this.cartElement.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          this.cartElement.setActiveElement(document.activeElement);
        }

        const config = fetchConfig();
        delete config.headers['Content-Type'];

        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          ...config,
          body: formData,
        });

        return response.json();
      }

      setSubmitLoading(isLoading) {
        this.submitButtonElement.setAttribute('aria-disabled', isLoading);
        this.submitButtonElement.classList.toggle('product-purchase__submit--loading', isLoading);
        if (this.spinnerElement) this.spinnerElement.hidden = !isLoading;
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message || '';
        this.errorElement.hidden = !message;
      }
    }
  );
}

if (!customElements.get('purchase-gifts')) {
  customElements.define(
    'purchase-gifts',
    class PurchaseGifts extends HTMLElement {
      connectedCallback() {
        this.tooltipElement = this.querySelector('[data-gift-tooltip]');
        if (!this.tooltipElement) return;

        this.toggleButtonElement = this.querySelector('[data-gift-tooltip-toggle]');
        this.sliderElement = this.querySelector('[data-slider-selector]');
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

        this.toggleButtonElement?.addEventListener('click', () => {
          this.toggle(!this.tooltipElement.classList.contains('gift-tooltip--open'));
        });

        this.querySelectorAll('[data-purchase-gift]').forEach((giftElement) => {
          giftElement.querySelector('input')?.addEventListener('click', () => this.handleDoubleTap(giftElement));
        });
      }

      disconnectedCallback() {
        this.stopDismissListeners();
      }

      toggle(isOpen) {
        this.tooltipElement.classList.toggle('gift-tooltip--open', isOpen);
        this.toggleButtonElement?.setAttribute('aria-expanded', isOpen);

        if (!isOpen) {
          this.stopDismissListeners();
          return;
        }

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      handleDoubleTap(giftElement) {
        const tappedAt = Date.now();
        const isSecondTap = this.lastTappedGiftElement === giftElement && tappedAt - this.lastTappedAt < 400;
        this.lastTappedGiftElement = giftElement;
        this.lastTappedAt = tappedAt;
        if (!isSecondTap) return;

        this.lastTappedAt = 0;
        this.slideTo(giftElement.dataset.giftProductId);
        this.toggle(true);
      }

      slideTo(giftProductId) {
        const swiper = this.sliderElement?.swiper;
        if (!swiper || !giftProductId) return;

        const index = Array.from(swiper.slides).findIndex(
          (slideElement) => slideElement.dataset.giftProductId === giftProductId
        );
        if (index >= 0) swiper.slideTo(index);
      }

      stopDismissListeners() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.toggle(false);
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        this.toggle(false);
      }
    }
  );
}